import { generatePath as generatePathRRD } from 'react-router-dom'
import type { ExtractRouteParams } from 'react-router'

export const BASE_PATH = '/supplier'

export const CLIENT_BASE_PATH = `${BASE_PATH}/clients/:documentNumber`

export const TINO_CREDIT_BASE_PATH = `${CLIENT_BASE_PATH}/tino-credit`
export const TINO_CREDIT_INVOICE_BASE_PATH = `${TINO_CREDIT_BASE_PATH}/payment-link/detail/:paymentLinkId`
export const TINO_CREDIT_INVOICE_RESERVATION_BASE_PATH = `${TINO_CREDIT_INVOICE_BASE_PATH}/limit-reservation/:limitReservationId`

export const INVOICE_BASE_PATH = `${BASE_PATH}/invoices`
export const INVOICE_DETAIL_BASE_PATH = `${INVOICE_BASE_PATH}/detail/:paymentLinkId`
export const INVOICE_DETAIL_RESERVATION_BASE_PATH = `${INVOICE_DETAIL_BASE_PATH}/limit-reservation/:limitReservationId`
export const INVOICE_UPLOAD_BASE_PATH = `${INVOICE_BASE_PATH}/upload/:paymentLinkId`

export const paths = {
  root: '/',

  // Public Routes
  goTo: '/go-to',
  error: '/error',
  blockedByNetwork: '/blocked-by-network',

  appStoreRedirect: '/app-store-redirect',
  partners: '/partners',
  optIn: '/opt-in',
  ownerBiometrics: '/owner-biometrics',
  qsa: '/qsa',
  loginPhoneCampaing: '/login-phone-campaing',
  crossSellLead: '/cross-sell-lead',
  onboarding: '/onboarding',
  checkout: '/checkout',
  limitReservation: '/limit-reservation',

  // Apps
  supplier: '/supplier',
  merchant: '/merchant/:companyId?',
  salesperson: '/salesperson/:companyId?',

  // Auth Routes
  auth: '/auth',
  login: '/login',
  loginByEmail: '/login-by-email',
  loginByPhone: '/login-by-phone',
  recoveryPassword: '/recovery/password',
  signUp: '/v2/sign-up',

  redirect: '/redirect/:companyId?',

  // Client List
  clients: `${BASE_PATH}/clients`,

  // Client Profile Pages
  client: `${CLIENT_BASE_PATH}/:section*`,
  clientOverview: `${CLIENT_BASE_PATH}/overview`,
  clientProfile: `${CLIENT_BASE_PATH}/profile`,
  creditIntelligence: `${CLIENT_BASE_PATH}/credit-intelligence`,
  commercialRelationship: `${CLIENT_BASE_PATH}/commercial-relationship`,
  tinoCredit: TINO_CREDIT_BASE_PATH,
  tinoCreditInvoice: `${TINO_CREDIT_BASE_PATH}/payment-link/detail/:paymentLinkId`,
  pdfViewer: `${CLIENT_BASE_PATH}/pdf-viewer`,

  // Client Profile Modals
  clientProfileOptIn: `${CLIENT_BASE_PATH}/:section*/opt-in`,
  clientProfileChanges: `${CLIENT_BASE_PATH}/:section*/company-changes`,
  clientProfileSocialSuggestion: `${CLIENT_BASE_PATH}/:section*/social-suggestion`,
  clientProfileCreditHowItWorks: `${CLIENT_BASE_PATH}/:section*/how-it-works`,
  clientProfileRequestKYC: `${CLIENT_BASE_PATH}/:section*/request-kyc`,
  clientProfileAddressDivergence: `${CLIENT_BASE_PATH}/:section*/address-divergence`,
  clientProfileMenu: `${CLIENT_BASE_PATH}/:section*/menu`,
  creditIntelligenceMatrix: `${CLIENT_BASE_PATH}/:section*/matrix`,
  creditIntelligenceFinancialRestrictions: `${CLIENT_BASE_PATH}/:section*/financial-restrictions`,
  creditIntelligenceProtests: `${CLIENT_BASE_PATH}/:section*/protests`,
  creditIntelligenceQueryHistory: `${CLIENT_BASE_PATH}/:section*/query-history`,
  clientExporter: `${CLIENT_BASE_PATH}/:section*/export`,

  // Order
  clientProfileOrderCreate: `${CLIENT_BASE_PATH}/:section*/create-order/:orderId?`,
  clientProfileOrderCheckout: `${CLIENT_BASE_PATH}/:section*/checkout`,
  clientProfileOrderConfirm: `${CLIENT_BASE_PATH}/:section*/confirm-order`,

  // Tino Credit Invoices Modals
  tinoCreditInvoiceDetail: `${TINO_CREDIT_INVOICE_BASE_PATH}`,
  tinoCreditInvoiceCancelLimitReservation: `${TINO_CREDIT_INVOICE_RESERVATION_BASE_PATH}/cancel`,
  tinoCreditInvoiceCancel: `${TINO_CREDIT_INVOICE_RESERVATION_BASE_PATH}/nota-fiscal/:invoiceId/cancel`,
  tinoCreditInvoiceCanceled: `${TINO_CREDIT_INVOICE_RESERVATION_BASE_PATH}/nota-fiscal/:invoiceId/canceled`,
  tinoCreditInvoiceCsat: `${TINO_CREDIT_INVOICE_BASE_PATH}/csat`,
  tinoCreditInvoiceBill: `${TINO_CREDIT_INVOICE_RESERVATION_BASE_PATH}/bill`,
  tinoCreditInvoiceBilling: `${TINO_CREDIT_INVOICE_RESERVATION_BASE_PATH}/billing`,
  tinoCreditInvoiceEdit: `${TINO_CREDIT_INVOICE_RESERVATION_BASE_PATH}/nota-fiscal/:invoiceId/edit`,
  tinoCreditInvoiceUpload: `${TINO_CREDIT_INVOICE_BASE_PATH}/upload/:paymentLinkId`,
  tinoCreditInvoiceUploadError: `${TINO_CREDIT_INVOICE_BASE_PATH}/upload/:paymentLinkId/upload-error`,
  tinoCreditInvoiceUploadHelp: `${TINO_CREDIT_INVOICE_BASE_PATH}/upload/:paymentLinkId/help`,

  // Invoice Pages
  invoices: INVOICE_BASE_PATH,
  invoiceBill: `${INVOICE_DETAIL_RESERVATION_BASE_PATH}/bill`,
  invoiceBilling: `${INVOICE_DETAIL_RESERVATION_BASE_PATH}/billing`,
  invoiceCsat: `${INVOICE_DETAIL_BASE_PATH}/csat`,
  invoiceCancelLimitReservation: `${INVOICE_DETAIL_RESERVATION_BASE_PATH}/cancel`,
  invoiceCancel: `${INVOICE_DETAIL_RESERVATION_BASE_PATH}/nota-fiscal/:invoiceId/cancel`,
  invoiceCanceled: `${INVOICE_DETAIL_RESERVATION_BASE_PATH}/nota-fiscal/:invoiceId/canceled`,
  invoiceDetail: `${INVOICE_DETAIL_BASE_PATH}`,
  invoiceEdit: `${INVOICE_DETAIL_RESERVATION_BASE_PATH}/nota-fiscal/:invoiceId/edit`,
  invoiceUpload: `${INVOICE_UPLOAD_BASE_PATH}`,
  invoiceUploadError: `${INVOICE_UPLOAD_BASE_PATH}/error`,
  invoiceUploadHelp: `${INVOICE_UPLOAD_BASE_PATH}/help`,

  faq: `${BASE_PATH}/faq`,
  notifications: `${BASE_PATH}/notifications`,
  settings: `${BASE_PATH}/settings`,
  statements: `${BASE_PATH}/statements`,
  updatePassword: `${BASE_PATH}/settings/update-password`,
  createPaymentLink: `${BASE_PATH}/payment-link/create/:merchantDocumentNumber?`,
} as const

type PathsType = (typeof paths)[keyof typeof paths]

export function generatePath<T extends PathsType>(
  path: T,
  params?: ExtractRouteParams<T, string | number | boolean>
) {
  return generatePathRRD(path, params)
}
